/*
 * The ONE place the UI decides what a slot's state is.
 *
 * There are two independent concepts and they must never be merged - merging them is the mistake
 * this whole change exists to undo:
 *
 *   slot_active   ADMIN capacity. Written only behind /userDataManagement/** (ROLE_ADMIN). The
 *                 worker API uses it as a kill switch, and V1's own dashboard hides inactive slots
 *                 entirely. Production holds 1,162 de-provisioned slots. A customer must never be
 *                 able to move this, and the UI must never present it as something they can.
 *
 *   user_enabled  The CUSTOMER's own pause. A paused slot stays VISIBLE, because the customer needs
 *                 to be able to un-pause it. Null means running.
 *
 * The server folds its feature flag in before sending: with pausing disabled it returns
 * user_enabled as null, so a stale `false` stored from an earlier run can never make this page
 * claim "Paused" about a slot the fleet is actively running. `user_pause_enabled` is still checked
 * here so the two can never disagree.
 */

export const SLOT_STATE = {
  NOT_PROVISIONED: 'not_provisioned',
  PAUSED: 'paused',
  /*
   * The slot's own switch is ON, but the DEVICE it runs on is switched off, so it is not going to
   * run. A distinct state on purpose: showing it as "Paused" would be a lie about where the switch
   * is (turning this slot on again does nothing), and showing it as "Running" would be a lie about
   * what it is doing. The device is the outer of the two levels.
   */
  DEVICE_PAUSED: 'device_paused',
  RUNNING: 'running',
};

/** 'not_provisioned' | 'paused' | 'running'. Every slot surface must go through this. */
export function slotState(slot) {
  if (!slot) return SLOT_STATE.RUNNING;
  if (!slot.slot_active) return SLOT_STATE.NOT_PROVISIONED;
  // The slot's own switch first: a slot that is off is off, whatever its device is doing. Only a
  // slot that WOULD run is held by its device.
  if (slot.user_pause_enabled === true && slot.user_enabled === false) return SLOT_STATE.PAUSED;
  if (slot.device_user_enabled === false) return SLOT_STATE.DEVICE_PAUSED;
  return SLOT_STATE.RUNNING;
}

/** The device-wide pause note, for a slot that is on but whose phone is switched off. */
export const DEVICE_PAUSED_NOTE =
  'This slot is switched on, but its device is paused, so nothing will run. '
  + 'Turn the device back on from the Devices page.';

/** True only for slots actually doing work - excludes paused AND de-provisioned. */
export function isSlotRunning(slot) {
  return slotState(slot) === SLOT_STATE.RUNNING;
}

/**
 * Where the SLOT'S OWN switch sits, ignoring whether its device is holding it.
 *
 * <p>Different question from {@link isSlotRunning}: a device-paused slot is switched ON but is not
 * running. Anything deciding what a WRITE would do must ask this one - a bulk resume that counted
 * device-paused slots would promise to change N slots and then change none of them, because their
 * own switch was already on.
 */
export function isSlotSwitchedOn(slot) {
  const s = slotState(slot);
  return s !== SLOT_STATE.PAUSED && s !== SLOT_STATE.NOT_PROVISIONED;
}

/** Is the customer allowed to pause/un-pause this slot right now? */
export function canTogglePause(slot) {
  return !!slot && slot.slot_active === true && slot.user_pause_enabled === true;
}

/*
 * What the server can honestly promise. BC only polls statusCheck between sessions
 * (BC/SR2.java:1174 is its only call site - it does NOT poll inside a running session), so a pause
 * is picked up at the next session boundary, not mid-session. Saying "stops now" would be a lie.
 */
export const PAUSE_EFFECT_NOTE =
  'Takes effect between sessions - a session already running finishes normally.';

export const PAUSE_DISABLED_NOTE =
  'Pausing is not enabled on this server yet.';

export const NOT_PROVISIONED_NOTE =
  'This slot is not provisioned. Your administrator controls how many slots are active on your account.';
